// 1. Cart Context (src/contexts/CartContext.js)
import React, { createContext, useContext, useReducer, useEffect, useRef } from 'react';

const CartContext = createContext();

const cartReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_TO_CART':
      const existingItem = state.items.find(item => item.id === action.payload.id);
      if (existingItem) {
        return {
          ...state,
          items: state.items.map(item =>
            item.id === action.payload.id
              ? { ...item, quantity: item.quantity + 1, clubTier: action.payload.clubTier, clubTierDisplay: action.payload.clubTierDisplay, clubTierPrice: action.payload.clubTierPrice, clubTierPeriod: action.payload.clubTierPeriod, addon: action.payload.addon, sensorQty: action.payload.sensorQty }
              : item
          )
        };
      }
      return {
        ...state,
        items: [...state.items, { ...action.payload, quantity: 1 }]
      };
    
    case 'REMOVE_FROM_CART':
      return {
        ...state,
        items: state.items.filter(item => item.id !== action.payload)
      };
    
    case 'UPDATE_QUANTITY':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, quantity: action.payload.quantity }
            : item
        ).filter(item => item.quantity > 0)
      };
    
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(item =>
          item.id === action.payload.id
            ? { ...item, ...action.payload.changes }
            : item
        )
      };

    case 'CLEAR_CART':
      return {
        ...state,
        items: []
      };
    
    case 'LOAD_CART':
      return action.payload;
    
    default:
      return state;
  }
};

const initialState = {
  items: []
};

export const CartProvider = ({ children }) => {
  const [state, dispatch] = useReducer(cartReducer, initialState);
  const initialized = useRef(false);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('hca_cart');
    if (savedCart) {
      try {
        dispatch({ type: 'LOAD_CART', payload: JSON.parse(savedCart) });
      } catch {
        localStorage.removeItem('hca_cart');
      }
    }
    initialized.current = true;
  }, []);

  // Save to localStorage whenever cart changes — skip the initial empty state
  useEffect(() => {
    if (!initialized.current) return;
    localStorage.setItem('hca_cart', JSON.stringify(state));
  }, [state]);

 const addToCart = (product) => {
  // Ensure the price is a string before adding to cart
  const productWithPriceAsString = {
    ...product,
    price: product.price ? String(product.price) : '0',
  };
  dispatch({ type: 'ADD_TO_CART', payload: productWithPriceAsString });
};

  const removeFromCart = (productId) => {
    dispatch({ type: 'REMOVE_FROM_CART', payload: productId });
  };

  const updateQuantity = (productId, quantity) => {
    dispatch({ type: 'UPDATE_QUANTITY', payload: { id: productId, quantity } });
  };

  const updateItem = (productId, changes) => {
    dispatch({ type: 'UPDATE_ITEM', payload: { id: productId, changes } });
  };

  const clearCart = () => {
    dispatch({ type: 'CLEAR_CART' });
  };

  const getTotalItems = () => {
    return state.items.reduce((total, item) => total + item.quantity, 0);
  };

  const getTotalPrice = () => {
    return state.items.reduce((total, item) => {
        let price;
        if (typeof item.price === 'string') {
            // Price is a string, so we can use replace()
            price = parseFloat(item.price.replace(/[^0-9.-]+/g, '') || 0);
        } else {
            // Price is already a number, or another type
            price = parseFloat(item.price || 0);
        }
        return total + (price * item.quantity);
    }, 0);
};

  return (
    <CartContext.Provider value={{
      cartItems: state.items,
      addToCart,
      removeFromCart,
      updateQuantity,
      updateItem,
      clearCart,
      getTotalItems,
      getTotalPrice
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};